import { Link } from 'react-router';
import { BlogPostLayout } from '@/components/blog/blog-post-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function NewYearSamePotholesBlogPost() {
  return (
    <BlogPostLayout
      slug="new-year-same-potholes"
      title="New Year, Same Potholes: How to Protect Your Claim After Vehicle Damage"
      description="January pothole damage is common after freeze-thaw weather. Learn what to photograph, what damage to check, and how to protect a UK pothole claim."
      category="Pothole Claim Advice"
      readTime="6 min"
      datePublished="2026-01-03"
      dateModified="2026-01-03"
      image={{
        src: '/blog/new-year-same-potholes-1200.webp',
        srcSet:
          '/blog/new-year-same-potholes-800.webp 800w, /blog/new-year-same-potholes-1200.webp 1200w',
        sizes: '(min-width: 1024px) 896px, calc(100vw - 32px)',
        alt: 'Rain-filled pothole on a residential UK road in winter, showing the kind of road defect that can cause vehicle damage.',
        width: 1200,
        height: 630,
        caption: 'January freeze-thaw weather can turn small road cracks into potholes that damage tyres, wheels and suspension.',
      }}
      keywords={[
        'pothole damage claim',
        'January potholes',
        'vehicle damage compensation',
        'council pothole claim',
        'Highways Act 1980',
      ]}
      geoFocus={['England', 'Wales', 'UK local councils', 'National Highways']}
      atAGlance={[
        { label: 'Best first step', value: 'Stop safely and photograph the pothole, location, and vehicle damage.' },
        { label: 'Who to claim from', value: 'Usually the local council, or National Highways for motorways and major A-roads.' },
        { label: 'Key evidence', value: 'Photos, exact location, repair quote or invoice, and any prior pothole reports.' },
        { label: 'Timing', value: 'Act in days and weeks while the pothole and vehicle damage are still fresh.' },
      ]}
      sources={[
        { name: 'Highways Act 1980, Section 41', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
        { name: 'National Highways roads we manage', url: 'https://nationalhighways.co.uk/our-roads/roads-we-manage/' },
        { name: 'FixMyStreet pothole reports', url: 'https://www.fixmystreet.com/' },
      ]}
      faqs={[
        {
          question: 'Can I claim if my car was damaged by a pothole in January?',
          answer:
            'Yes, if the pothole was on a public road and the responsible authority failed to maintain the road to a reasonable standard. You still need evidence that links the pothole to the vehicle damage.',
        },
        {
          question: 'What should I photograph after hitting a pothole?',
          answer:
            'Photograph the pothole with something for scale, the wider location, nearby landmarks or signs, and close-ups of the vehicle damage. Timestamped and geotagged photos can help corroborate your account.',
        },
        {
          question: 'What if the council rejects my pothole claim?',
          answer:
            'A rejection is not always the end. Many councils rely on inspection and repair records. Prior reports, clear photos, and a garage assessment can give you stronger grounds to challenge the response.',
        },
      ]}
    >
      <p>
        The decorations are down, the resolutions are wobbling, and the roads are worse than ever.
        January is the worst month of the year for pothole damage, and it is not bad luck. It is
        physics. Water seeps into cracks in the road over autumn, freezes and expands in the winter
        cold, then thaws and leaves a hole behind. Repeat that cycle a few times through December
        and January and a hairline crack becomes a wheel-wrecking crater.
      </p>

      <p>
        So if you have started the new year with a thud, a bang and a steering wheel that no longer
        feels quite right, you are not alone. Here is what to do next, and how to protect your right
        to claim the cost back.
      </p>

      <h2>Can You Actually Claim for Pothole Damage?</h2>

      <p>
        Yes. If your vehicle is damaged by a pothole on a public road, you may be entitled to claim
        compensation from the authority responsible for maintaining that road. For most roads that is
        your local council. For motorways and major A-roads it is{' '}
        <a href="https://nationalhighways.co.uk/our-roads/roads-we-manage/" className={inlineLink} target="_blank" rel="noopener noreferrer">
          National Highways
        </a>
        .
      </p>

      <p>
        The legal basis is the{' '}
        <Link to="/guides/section-41-explained" className={inlineLink}>
          Highways Act 1980
        </Link>
        , which places a duty on the responsible authority to maintain the road. If they failed to
        maintain it to a reasonable standard and that failure caused your damage, you have grounds to
        claim.
      </p>

      <p>
        The important word is <strong>grounds</strong>. A claim is not automatic, and councils refuse
        far more than they pay. But a well-evidenced claim is a completely different proposition to a
        vague one, and most drivers who lose out do so not because they had no case, but because they
        did not protect it at the roadside. That is the part you control.
      </p>

      <h2>The First 10 Minutes Matter Most</h2>

      <p>
        What you do in the minutes after hitting a pothole has more impact on your claim than
        anything else. Once you have driven away and the council has filled the hole, the evidence is
        gone. So if it is safe to do so:
      </p>

      <ul>
        <li>
          <strong>Photograph the pothole.</strong> Get a clear shot of the hole itself, ideally with
          something for scale next to it: a shoe, a drink can, anything that shows its size and
          depth. Depth matters, because councils often argue a pothole was too shallow to be their
          responsibility.
        </li>
        <li>
          <strong>Photograph the location.</strong> Take a wider shot showing the road, any signs or
          landmarks, so the exact spot can be identified later.
        </li>
        <li>
          <strong>Photograph the damage.</strong> Capture close-ups of the damage to your vehicle,
          whether that is a buckled alloy, a split tyre, or scraping underneath.
        </li>
        <li>
          <strong>Note the details.</strong> Record the exact location, the date and time, and the
          weather and road conditions. Your phone will usually timestamp and geotag your photos
          automatically, which is useful corroboration.
        </li>
      </ul>

      <p>
        If it is not safe to stop, do not risk it. Come back later in daylight when you can pull over
        properly, and photograph the pothole then. For a fuller checklist, read Fixtyer's guide to{' '}
        <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
          photographing pothole damage
        </Link>
        .
      </p>

      <h2>Check for the Damage You Cannot See</h2>

      <p>
        Plenty of pothole damage is obvious. A flat tyre or a bent wheel announces itself. But the
        costly, dangerous damage often hides.
      </p>

      <p>
        A hard enough impact can knock your wheel alignment out, damage your suspension, or crack a
        spring without any dramatic symptom at the roadside. The car still drives, so you assume you
        got away with it. Then a few weeks later the steering pulls to one side, or your tyres start
        wearing unevenly on one edge, and the repair bill is far bigger than a single tyre would have
        been.
      </p>

      <p>
        If you have taken a serious knock, it is worth having the car checked over even if it feels
        fine. Not only for your safety, but because alignment and suspension damage are claimable
        too, provided you can link them to the incident. The sooner you get it inspected, the cleaner
        that link is.
      </p>

      <h2>Get the Damage Assessed and Keep Every Receipt</h2>

      <p>
        To claim, you need to show what the damage cost to put right. That means a written quote or
        invoice from a garage describing the work and linking it to impact damage. Keep everything:
        the assessment, the repair invoice, receipts for a replacement tyre, even the cost of
        recovery if your car was undriveable.
      </p>

      <p>
        If a mechanic notes that the damage is consistent with a pothole impact rather than general
        wear, that is worth having in writing. The council's most common line of defence is that the
        damage was pre-existing, so anything that ties it to a sudden impact strengthens your
        position. Fixtyer's guide to{' '}
        <Link to="/guides/getting-repair-quotes" className={inlineLink}>
          getting the right repair quote
        </Link>{' '}
        explains what a useful garage assessment should include.
      </p>

      <h2>Report the Pothole, and Check if Others Already Have</h2>

      <p>
        Reporting the pothole to the council does two things. It creates a record, and it can
        strengthen your claim, because authorities have a stronger duty once a defect has been
        formally reported.
      </p>

      <p>
        Even better is finding out whether the pothole was already reported before you hit it. If a
        council was told about a dangerous pothole weeks or months ago and did nothing, a claim that
        the road was not reasonably maintained becomes much harder for them to dismiss. Public
        reporting tools and the council's own records can reveal this history. Start with Fixtyer's
        guide to{' '}
        <Link to="/guides/prior-reports-explained" className={inlineLink}>
          why prior pothole reports matter
        </Link>
        .
      </p>

      <h2>How Long Do You Have to Claim?</h2>

      <p>
        Do not sit on it. While the formal legal time limit for this kind of claim can be up to six
        years, leaving it that long is a mistake. Evidence disappears, potholes get filled, and
        memories fade. A claim submitted while the damage is fresh and the pothole still visible is
        far stronger than one assembled months later. Treat it as something to deal with in days and
        weeks, not years.
      </p>

      <h2>What if the Council Says No?</h2>

      <p>
        Many first responses are rejections, often citing a defence that the authority had a
        reasonable inspection and repair system in place. This is not necessarily the end of the
        road. A rejection can be challenged, particularly if you can show the pothole had been
        reported previously or had clearly been present for some time. A well-documented claim,
        submitted clearly and backed by evidence, is much harder to brush aside than a one-line
        complaint.
      </p>

      <p>
        If you receive that kind of response, read Fixtyer's guides to the{' '}
        <Link to="/guides/section-58-defence" className={inlineLink}>
          Section 58 defence
        </Link>{' '}
        and{' '}
        <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
          common rejection reasons
        </Link>{' '}
        before deciding what to do next.
      </p>

      <h2>The Bottom Line</h2>

      <p>
        A new year does not bring new roads, and January will keep producing potholes until the
        weather turns. You cannot control the state of the roads. You can control whether you are out
        of pocket because of them.
      </p>

      <p>
        If you have hit a pothole: stop safely, photograph everything, get the damage checked
        properly, keep your paperwork, and act while the evidence is fresh. Do that, and a
        frustrating start to the year does not have to be an expensive one too.
      </p>

      <div className="bg-brand-50 border border-brand-200 rounded-2xl p-6">
        <p className="text-sm text-surface-700 leading-relaxed">
          Fixtyer helps UK drivers prepare and submit their own pothole damage claims, without
          solicitors and without commission, so you keep 100% of any compensation. Fixtyer provides
          documents and guidance only and does not provide legal advice. If you are unsure about your
          individual circumstances, seek independent legal advice.
        </p>
      </div>
    </BlogPostLayout>
  );
}
